<template>
  <div class="event-occurrence">
    <toolbar transparent fixed :logo="false">
      <template v-slot:right>
        <toolbar-share-button />
      </template>
    </toolbar>

    <template v-if="isPresent(banner) || isPresent(video)">
      <hero :image="banner" :video="video" />
    </template>
    <template v-else>
      <div class="v-toolbar__spacer"></div>
    </template>

    <text-block-header :heading="title" no-collapse>
      <div class="event-occurrence__icon-row mt-1">
        <span>
          <v-icon icon="today" />
        </span>
        <span>
          {{ toDate(starts_at) }}
        </span>
      </div>
      <div class="event-occurrence__icon-row">
        <span>
          <v-icon icon="schedule" />
        </span>
        <span>
          {{ parseEventTime(starts_at, ends_at, is_all_day) }}
        </span>
      </div>
      <div v-if="address" class="event-occurrence__icon-row">
        <span>
          <v-icon icon="place" />
        </span>
        <span class="event-occurrence__info">
          {{ address }}
        </span>
      </div>
      <div v-if="is_virtual" class="event-occurrence__icon-row">
        <v-icon icon="airplay" />
        <span>
          <span>
            Livestream via
          </span>
          <a class="event-occurrence__stream-link">
            {{ getHostName(stream_link) }}
          </a>
        </span>
      </div>
      <div v-if="is_virtual && isRegistered" class="event-occurrence__icon-row">
        <span></span>
        <span>
          <a :href="stream_link" target="_blank">{{ stream_link }}</a>
        </span>
      </div>
    </text-block-header>

    <div v-if="showMap" class="event-occurrence__map">
      <location-map :lat="lat" :lng="lng" />
    </div>

    <template v-if="isPresent(description)">
      <div class="bg-white">
        <text-block
          content="<h2>Event Details</h2>"
          tag="h2"
          :space-top-ratio="150"
        />
        <text-block :content="description" />
      </div>
    </template>

    <template v-if="hasEnded">
      <cta-row white-bg row>
        <cta-row-status
          icon="history"
          heading="This event has ended"
          subheading="Browse other events"
        />
      </cta-row>
    </template>

    <template v-else-if="is_cancelled">
      <cta-row white-bg row>
        <cta-row-status
          icon="highlight_off"
          heading="Event has been cancelled"
          subheading="Browse other events"
        />
      </cta-row>
    </template>

    <template v-else-if="isRegistered">
      <cta-row white-bg row>
        <cta-row-status
          icon="done_all"
          heading="You're going!"
          subheading="We'll remind you before the event"
        />
      </cta-row>
    </template>

    <template v-else-if="showCtaRow">
      <cta-row white-bg row>
        <cta-row-price :priceText="priceText" subText="RSVP Today" />
        <v-button
          class="mr-0"
          :disabled="isFull"
          :is-loading="isLoading"
          @click="handleCtaClick"
        >
          {{ ctaText }}
        </v-button>
      </cta-row>

      <template v-if="is_paid && isPresent(product)">
        <checkout-modal :product="product" @success="handleEventPurchase" />
      </template>
    </template>

    <alert-dialog
      :actions="[
        {
          title: 'View Receipt',
          callback: this.goToAccount
        },
        {
          title: 'Go To Event',
          callback: () => (this.showSuccessDialog = false)
        }
      ]"
      :show="showSuccessDialog"
      @close="() => (this.showSuccessDialog = false)"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-semi-bold">
        Thanks for your purchase. You successfully registered for this event!
      </p>
    </alert-dialog>

    <v-powered-by-fitter />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import { isPresent, getHostName } from "@/lib/utils"
import { toDate, parseEventTime, isPast } from "@/lib/date-utils"
import Toolbar from "@/components/Toolbar"
import ToolbarShareButton from "@/components/toolbar/ShareButton"
import Hero from "@/components/Hero"
import TextBlock from "@/components/TextBlock"
import TextBlockHeader from "@/components/TextBlockHeader"

export default {
  components: {
    Toolbar,
    ToolbarShareButton,
    Hero,
    TextBlock,
    TextBlockHeader,
    LocationMap: () => import("@/components/LocationMap"),
    CheckoutModal: () => import("@/components/checkout/CheckoutModal"),
    CtaRow: () => import("@/components/CtaRow"),
    CtaRowPrice: () => import("@/components/cta-row/Price"),
    CtaRowStatus: () => import("@/components/cta-row/Status"),
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    id: { type: [Number, String], required: true },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    banner: { type: Object, default: () => ({}) },
    video: { type: Object, default: () => ({}) },
    starts_at: { type: String, required: true },
    ends_at: { type: String, required: true },
    is_all_day: { type: Boolean, default: false },
    is_cancelled: { type: Boolean, default: false },
    address: { type: String, default: null },
    is_paid: { type: Boolean, required: true },
    is_virtual: { type: Boolean, required: true },
    stream_link: { type: String, default: "" },
    lat: { type: String, default: null },
    lng: { type: String, default: null },
    product: { type: Object, default: null }
  },

  data() {
    return {
      isLoading: false,
      showSuccessDialog: false,
      isFull: false,
      hasAccessViaPack: false
    }
  },

  mounted() {
    if (!this.isRegistered) {
      this.isLoading = true
      this.getEventOccurrenceAccess(this.id)
        .then(res => {
          this.isFull = res.data.is_full
          this.hasAccessViaPack = res.data.has_access_via_pack
        })
        .finally(() => (this.isLoading = false))
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("events", ["isUserSubscribedToEventOccurrence"]),
    ...mapGetters("access", ["hasAccessViaSubscription"]),

    showMap() {
      return !this.is_virtual && this.lat && this.lng
    },

    priceText() {
      if (this.canAccessFree) {
        return "Free"
      } else if (this.hasAccessViaPack) {
        return `1 x Pass`
      } else {
        return `${this.product.symbol || "$"}${this.product.price_dollars}`
      }
    },

    ctaText() {
      if (this.isFull) {
        return "Sold out"
      } else {
        return "Register Now"
      }
    },

    canAccessFree() {
      return !this.is_paid || this.hasAccessViaSubscription
    },

    isRegistered() {
      return this.isUserSubscribedToEventOccurrence(this.id)
    },

    hasEnded() {
      return isPast(this.ends_at)
    },

    showCtaRow() {
      return !this.isRegistered
    }
  },

  methods: {
    isPresent,
    toDate,
    parseEventTime,
    getHostName,

    ...mapActions("events", [
      "subscribe",
      "fetchSubscriptions",
      "getEventOccurrenceAccess"
    ]),

    handleCtaClick() {
      // TODO: add tests
      if (
        this.is_paid &&
        !(this.hasAccessViaSubscription || this.hasAccessViaPack)
      ) {
        this.$router.push("#checkout")
      } else if (!this.isAuthenticated) {
        this.$router.push("#auth")
      } else {
        this.isLoading = true

        this.subscribe(this.id)
          .catch(err => {
            this.$notify(err.response.data.message)
          })
          .finally(() => (this.isLoading = false))
      }
    },

    handleEventPurchase() {
      this.showSuccessDialog = true
      this.isLoading = true
      this.fetchSubscriptions().finally(() => (this.isLoading = false))
    },

    goToAccount() {
      this.showSuccessDialog = false
      this.$router.push("/me/account")
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.event-occurrence__icon-row {
  color: $color-ui--grey-70;
  font-weight: $font-weight--semi-bold;
  padding: $size--2 0;
  display: grid;
  grid-template-columns: 2.5rem auto;
  align-items: flex-start;

  :first-child {
    text-align: center;
  }

  .v-icon {
    color: $color-ui--grey-50;
    font-size: 1.5em;
    margin-right: $size--3;
  }
}

.event-occurrence__stream-link {
  color: $color-ui--black;
  font-weight: $font-weight--bold;
  text-decoration: none;
}
</style>
